import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, PenLine } from "lucide-react";

import RhythmSections from "@/components/RhythmSections";
import RhythmCoverStar from "@/components/RhythmCoverStar";
import { CoverLines } from "@/components/RhythmCoverLines";
import VoteBanner from "@/components/VoteBanner";
import RhythmReviews from "@/components/RhythmReviews";
import RhythmSubmit from "@/components/RhythmSubmit";
import RhythmPress from "@/components/RhythmPress";
import { fadeUp } from "@/lib/motion";
import SpinRating from "@/components/SpinRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EqBars() {
  return (
    <span className="flex h-3 items-end gap-[3px]" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className="eq-bar w-[3px] rounded-sm bg-[#F3F1ED]"
          style={{ height: "100%", animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </span>
  );
}

function Barcode() {
  return (
    <div data-testid="rhythm-barcode" className="flex items-stretch gap-[2px] bg-white px-2.5 py-1.5 h-12">
      {[3, 1, 2, 1, 4, 1, 1, 3, 2, 1, 5, 1, 2, 2, 1, 4, 1, 1, 2, 3, 1, 2, 1, 1, 4, 2, 1, 3, 1, 1, 2, 4].map((w, i) => (
        <span key={i} style={{ width: w * 1.5 }} className={i % 2 ? "bg-white" : "bg-[#0B0A0F]"} />
      ))}
    </div>
  );
}

function FeaturedStory({ item }) {
  return (
    <section id="featured" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="grid gap-8 lg:grid-cols-12 lg:gap-14"
      >
        <div className="lg:col-span-7">
          <div className="rhythm-frame relative rounded-[26px]">
            {parseFloat(item.score) / 2 >= 4.75 && (
              <div
                data-testid="rhythm-classic-stamp"
                className="absolute -top-4 right-6 z-10 rotate-6 border-4 border-[#F3F1ED] bg-[#0B0A0F]/85 px-4 py-1.5 font-rhythm-display text-lg uppercase tracking-[0.1em] text-[#F3F1ED] shadow-lg backdrop-blur"
              >
                Classic
              </div>
            )}
            <img
              data-testid="rhythm-featured-image"
              src={item.image_url}
              alt={`${item.artist} — ${item.title}`}
              className="aspect-square w-full rounded-[18px] object-cover"
            />
          </div>
        </div>
        <div className="flex flex-col justify-center lg:col-span-5">
          <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
            Cover Story · {item.week}
          </p>
          <h2 className="mt-4 font-rhythm-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-4xl lg:text-[2.8rem]">
            {item.artist}
          </h2>
          <h3 className="mt-3 font-garamond text-2xl italic text-[#F3F1ED]/85 sm:text-3xl">
            {item.review_title}
          </h3>
          <p className="mt-6 text-base leading-relaxed text-white/70">{item.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <SpinRating score={item.score} size={20} />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">
              {item.ptype} · {item.genre} · {item.year}
            </span>
          </div>
          <p className="mt-5 font-garamond text-lg italic text-white/70">
            Reviewed by Morgan Williams
          </p>
          <Link
            data-testid="rhythm-featured-read-more"
            to={`/rhythm/${item.slug}`}
            className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#F3F1ED] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0B0A0F] transition-transform duration-300 hover:translate-x-1"
          >
            Read More <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

function SpecialFeature({
  item,
  id = "special",
  testid = "rhythm-special-feature",
  kicker = "Special Feature",
}) {
  if (!item) return null;
  const paras = (item.body || "").split("\n\n");
  const noteIdx = paras.findIndex((p) => p.startsWith("EDITORS NOTE"));
  const main = noteIdx >= 0 ? paras.slice(0, noteIdx) : paras;
  const note =
    noteIdx >= 0
      ? paras[noteIdx].replace(/^EDITORS NOTE\s*—\s*[^—]+\s*—\s*/, "")
      : "";
  return (
    <section
      id={id}
      data-testid={testid}
      className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24"
    >
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="border border-white/15"
      >
        <div className="flex items-center justify-between border-b border-white/15 bg-[#F3F1ED] px-5 py-2.5">
          <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#0B0A0F]">
            {kicker}
          </p>
          <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#0B0A0F]">
            {item.ptype} · {item.week}
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div className="relative min-w-0">
            <img
              data-testid="rhythm-special-photo"
              src={item.image_url}
              alt={`${item.artist} — ${item.title}`}
              className="h-64 w-full object-cover lg:h-full"
            />
            <span className="absolute bottom-3 left-3 bg-[#0B0A0F]/80 px-3 py-1.5 font-rhythm-mono text-[9px] uppercase tracking-[0.18em] text-[#F3F1ED]">
              {item.year} · {item.genre}
            </span>
          </div>
          <div className="p-7 sm:p-10">
            <p className="font-rhythm-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
              {item.artist}
            </p>
            <h3 className="mt-3 break-words font-rhythm-display text-3xl uppercase leading-none tracking-tight sm:text-4xl">
              {item.title}
            </h3>
            <p className="mt-3 font-garamond text-lg italic text-white/70">
              {item.review_title}
            </p>
            <div className="mt-6 space-y-4">
              {main.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-white/80 sm:text-base">
                  {p}
                </p>
              ))}
            </div>
            {note && (
              <div
                data-testid="rhythm-special-note"
                className="mt-6 border-l-4 border-[#F3F1ED] bg-white/[0.05] p-5"
              >
                <p className="font-rhythm-mono text-[9px] uppercase tracking-[0.2em] text-white/50">
                  Editor's Note — Morgan Chanel
                </p>
                <p className="mt-2 font-garamond text-lg italic leading-relaxed text-[#F3F1ED]">
                  {note}
                </p>
              </div>
            )}
            <p className="mt-6 font-rhythm-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
              {item.verdict}
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function Movement({ p }) {
  if (p.last_position == null)
    return <span className="font-rhythm-mono text-[9px] font-bold text-[#34D399]">NEW</span>;
  if (p.position < p.last_position) return <span className="text-[#34D399]">▲</span>;
  if (p.position > p.last_position) return <span className="text-[#FB7185]">▼</span>;
  return <span className="text-white/40">—</span>;
}

function ChartWatch({ reviews, artPack }) {
  const { data } = useQuery({
    queryKey: ["rhythm-charts"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/rhythm/charts`);
        return data;
      } catch {
        return null;
      }
    },
    refetchInterval: 300_000,
  });
  const placements = data?.placements || [];
  const featuredReviews = (reviews || []).slice(0, 5);
  const updated = data?.updated_at ? new Date(data.updated_at) : null;

  return (
    <section id="charts" data-testid="rhythm-charts" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-4 border-[#F3F1ED] pb-5">
        <div>
          <p
            className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]"
            style={{ color: "#34D399" }}
          >
            Live · Refreshed hourly
          </p>
          <h2 className="mt-3 font-rhythm-display text-3xl uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Chart Watch
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/55">
            Current chart positions for the Issue 03 cover package — Billboard
            Hot 100 &amp; 200, Spotify Top 50, Apple Music Top Songs &amp;
            Albums — tracked live while they're in review.
          </p>
        </div>
        {updated && !isNaN(updated.getTime()) && (
          <span
            data-testid="rhythm-charts-updated"
            className="font-rhythm-mono text-[9px] uppercase tracking-[0.2em] text-white/40"
          >
            Updated {updated.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ·{" "}
            {updated.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>

      {/* COVER PACKAGE — chart report for the featured artists */}
      <div data-testid="rhythm-cover-chart-report" className="mt-9 border-t-4 border-[#F3F1ED]">
        <p className="border-b border-white/15 py-3 font-rhythm-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
          Issue 03 Cover Package — the artists you're featuring
        </p>
        {featuredReviews.map((r, idx) => {
          const mine = placements.filter((p) => p.slug === r.slug);
          return (
            <Link
              key={r.slug}
              data-testid={`rhythm-feature-chart-${idx + 1}`}
              to={`/rhythm/${r.slug}`}
              className="group flex items-center gap-4 border-b border-white/10 py-4 transition-colors hover:bg-white/[0.03] sm:gap-5"
            >
              <img
                src={artPack?.[r.slug]?.artwork_url || r.image_url}
                alt={`${r.artist} — ${r.title} album art`}
                className="h-14 w-14 shrink-0 rounded-lg object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-rhythm-display text-lg uppercase text-[#F3F1ED] transition-colors group-hover:text-[#F3F1ED] sm:text-xl">
                  {r.artist}
                </span>
                <span className="block truncate font-garamond text-base italic text-white/60">
                  {r.title}
                </span>
              </span>
              <span className="shrink-0 text-right">
                {mine.length > 0 ? (
                  mine.map((p) => (
                    <span
                      key={p.chart}
                      className="block font-rhythm-mono text-[10px] uppercase tracking-[0.12em] text-[#F3F1ED]"
                    >
                      #{p.position} {p.chart}
                    </span>
                  ))
                ) : (
                  <span className="block font-garamond text-base italic text-white/50">
                    Not on the boards this week.
                  </span>
                )}
              </span>
            </Link>
          );
        })}
      </div>

      {/* FULL BOARD */}
      <div className="mt-12">
        <h3 className="font-rhythm-display text-2xl uppercase tracking-tight sm:text-3xl">
          The Full Board
        </h3>
        <p className="mt-2 font-rhythm-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
          Every reviewed project, all tracked charts
        </p>
      </div>
      {placements.length === 0 ? (
        <p data-testid="rhythm-charts-empty" className="mt-9 font-garamond text-xl italic text-white/60">
          No placements on the boards yet. Tracking starts the moment a reviewed
          project charts — watch this space.
        </p>
      ) : (
        <div className="mt-2">
          {placements.map((p, i) => (
            <Link
              key={`${p.slug}-${p.chart}-${p.position}`}
              data-testid={`rhythm-chart-row-${i}`}
              to={`/rhythm/${p.slug}`}
              className="group flex items-center gap-4 border-b border-white/10 py-4 transition-colors hover:bg-white/[0.03] sm:gap-6"
            >
              <span className="w-14 shrink-0 text-center">
                <span className="block font-rhythm-display text-2xl leading-none text-[#F3F1ED] sm:text-3xl">
                  {p.position}
                </span>
                <span className="mt-1 block text-[10px]">
                  <Movement p={p} />
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-rhythm-display text-lg uppercase leading-tight text-[#F3F1ED] transition-colors group-hover:text-[#F3F1ED] sm:text-xl">
                  {p.artist} <span className="text-[#F3F1ED]">·</span> {p.project}
                </span>
                <span className="mt-1 block truncate font-rhythm-mono text-[9px] uppercase tracking-[0.16em] text-white/40">
                  {p.matched_title} · LW {p.last_position ?? "—"} · PEAK {p.peak_position} ·{" "}
                  {p.weeks_on_chart} {p.weeks_on_chart === 1 ? "WK" : "WKS"}
                </span>
              </span>
              <span className="hidden shrink-0 rounded-full border border-[#F3F1ED]/40 px-3 py-1.5 font-rhythm-mono text-[9px] uppercase tracking-[0.16em] text-[#F3F1ED] md:block">
                {p.chart}
              </span>
            </Link>
          ))}
        </div>
      )}

    </section>
  );
}

export default function RhythmReportPage() {
  const { data: items = [] } = useQuery({
    queryKey: ["rhythm"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/rhythm`);
        return data?.length ? data : [];
      } catch {
        return [];
      }
    },
  });

  const reviews = items.filter((i) => i.kind === "review" && !i.archived);
  const archivedReviews = items.filter((i) => i.kind === "review" && i.archived);
  const special = items.find((i) => i.kind === "special");
  const coverStory = items.find((i) => i.kind === "cover-story");
  const coverPackage = reviews.slice(0, 5);
  const featured =
    items.find((i) => i.featured && i.kind === "review") || reviews[0];

  const { data: artPack = {} } = useQuery({
    queryKey: ["rhythm-artwork-pack", coverPackage.map((r) => r.slug).join("|")],
    queryFn: async () => {
      const results = await Promise.all(
        coverPackage.map((r) =>
          axios
            .get(`${API}/rhythm/${r.slug}/artwork`)
            .then((res) => [r.slug, res.data])
            .catch(() => [r.slug, null])
        )
      );
      return Object.fromEntries(results);
    },
    enabled: coverPackage.length > 0,
  });

  const { data: chartData } = useQuery({
    queryKey: ["rhythm-charts"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/rhythm/charts`);
        return data;
      } catch {
        return null;
      }
    },
    refetchInterval: 300_000,
  });
  const tops = chartData?.tops || {};

  const toc = [
    ...coverPackage.map((r) => ({
      label: `${r.artist} — ${r.title}`,
      kind: "Review",
      href: `/rhythm/${r.slug}`,
    })),
    { label: "Cover Story", kind: "The Cover Story", href: "#cover-story" },
    { label: "Special Feature", kind: "The Event", href: "#special" },
    { label: "New Music Friday", kind: "Department", href: "#friday" },
    { label: "Artist Spotlights", kind: "Department", href: "#spotlights" },
    { label: "Latest Reviews — The Archive", kind: "Department", href: "#reviews" },
    { label: "Chart Watch", kind: "Live Board", href: "#charts" },
    { label: "Submit Your Music", kind: "Open Call", href: "#submit" },
    { label: "Press & Interviews", kind: "Contact", href: "#press" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0A0F] font-rhythm text-[#F3F1ED]">
      <VoteBanner />
      <div className="border-b border-black/30 bg-[#F3F1ED] text-[#0B0A0F]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-5 py-1.5 font-rhythm-mono text-[10px] uppercase tracking-[0.18em]">
          <span data-testid="rhythm-issue-vol">Vol. 1 — Issue 03</span>
          <span className="hidden sm:inline">October 2026</span>
          <span className="hidden md:inline">Morgan Chanel — Editor-in-Chief</span>
          <span>$4.95 US · $5.95 CAN</span>
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0A0F]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
          <Link
            data-testid="rhythm-back-to-cgd"
            to="/"
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-[#F3F1ED]"
          >
            <ArrowRight className="hidden h-4 w-4" /> City Girls Decoded
          </Link>
          <span className="hidden font-garamond text-lg italic text-white/70 md:block">
            Music. Criticism. Discovery.
          </span>
          <span
            data-testid="rhythm-live-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#F3F1ED]/40 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3F1ED]"
          >
            <EqBars /> On Air
          </span>
        </div>
      </header>

      <main>
        {/* THE COVER */}
        <section data-testid="rhythm-cover" className="relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 38%, rgba(255,255,255,0) 62%, rgba(255,255,255,0.06) 100%)",
            }}
          />
          <div className="relative mx-auto flex min-h-[88vh] max-w-7xl flex-col px-5 pb-10 pt-10 sm:pt-14">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <p
                data-testid="rhythm-masthead-the"
                className="font-rhythm-mono text-xs font-bold uppercase tracking-[0.5em] text-white/60"
              >
                The
              </p>
              <div className="relative mt-3 inline-block">
                {/* cherry offset shadow — print misregistration, like the cover block */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 rotate-[1.2deg] bg-[#FF0055]"
                />
                <h1
                  data-testid="rhythm-masthead"
                  className="relative -rotate-[1.2deg] bg-[#F3F1ED] px-6 py-4 uppercase leading-[0.84] tracking-tight text-[#0B0A0F] sm:px-9 sm:py-6"
                  style={{ fontFamily: '"Archivo Black", Archivo, sans-serif' }}
                >
                  <span className="block text-5xl sm:text-7xl lg:text-[6rem]">
                    Rhythm
                  </span>
                  <span className="block text-5xl sm:text-7xl lg:text-[6rem]">
                    Report<span className="align-super text-[0.35em]">™</span>
                  </span>
                </h1>
                <p
                  className="absolute -right-4 -top-8 rotate-[6deg] font-rhythm-marker text-xl text-[#FF0055] sm:text-3xl"
                  style={{ textShadow: "0 2px 0 rgba(11,10,15,.85)" }}
                >
                  the music magazine!
                </p>
              </div>
              <div className="mt-6 flex items-center gap-5">
                <span className="h-[6px] w-16 bg-[#F3F1ED]" />
                <p className="font-rhythm-mono text-xs uppercase tracking-[0.3em] text-white/75 sm:text-sm">
                  Music · Criticism · Discovery
                </p>
              </div>
              <p
                data-testid="rhythm-intro-line"
                className="mt-6 max-w-xl text-sm leading-relaxed text-white/60"
              >
                The Rhythm Report is City Girls Decoded's music vertical —
                unfiltered reviews scored in spins, a live chart board, and
                artist spotlights. Fridays are for who's dropping and the
                debut numbers; Mondays are for the editor's notes.
              </p>
            </motion.div>

            {/* THE COVER — cover star + cover lines (the quotes are site navigation) */}
            <div className="mt-10 grid gap-8 lg:grid-cols-12 lg:items-stretch">
              <div className="lg:col-span-5">
                <RhythmCoverStar lead={coverPackage[0]} />
              </div>
              <div className="lg:col-span-7">
                <CoverLines />
              </div>
            </div>

            {/* THE COVER STORY — this week's cover star's write-up, first thing on the page */}
            {coverStory && (
              <SpecialFeature
                item={coverStory}
                id="cover-story"
                testid="rhythm-cover-story"
                kicker="The Cover Story"
              />
            )}

            {/* THE COVER PACKAGE — names cover */}
            {coverPackage.length > 0 ? (
              <nav
                data-testid="rhythm-cover-stack"
                className="mt-10 border-t-4 border-[#F3F1ED] lg:mt-12"
              >
                {coverPackage.map((item, idx) => (
                  <Link
                    key={item.slug}
                    data-testid={`rhythm-cover-row-${idx + 1}`}
                    to={`/rhythm/${item.slug}`}
                    className="group flex items-center gap-3 border-b border-white/15 py-3.5 transition-colors hover:bg-white/[0.04] sm:gap-5 sm:py-5"
                  >
                    <span className="shrink-0 font-rhythm-mono text-[10px] font-bold text-[#F3F1ED]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <img
                      data-testid={`rhythm-cover-art-${idx + 1}`}
                      src={artPack[item.slug]?.artwork_url || item.image_url}
                      alt={`${item.artist} — ${item.title} album art`}
                      className="h-10 w-10 shrink-0 rounded-lg object-cover sm:h-16 sm:w-16"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-rhythm-display text-xl uppercase leading-tight text-[#F3F1ED] transition-colors duration-200 group-hover:text-[#F3F1ED]/70 sm:text-4xl lg:text-[2.7rem]">
                        {item.artist}
                      </span>
                      <span className="mt-1 block truncate font-rhythm-mono text-[9px] uppercase tracking-[0.14em] text-white/55 sm:hidden">
                        {item.title} · {item.genre}
                      </span>
                    </span>
                    <span className="hidden max-w-[42%] shrink-0 truncate text-right font-rhythm-mono text-[11px] uppercase tracking-[0.18em] text-white/55 sm:block">
                      {item.title} · {item.genre}
                    </span>
                  </Link>
                ))}
              </nav>
            ) : (
              <p
                data-testid="rhythm-cover-empty"
                className="mt-12 border-t-4 border-[#F3F1ED] py-10 font-garamond text-xl italic text-white/60"
              >
                Issue 03 is at the printer — the cover package lands here first.
              </p>
            )}

            {/* cover bottom strip */}
            <div className="mt-auto flex flex-wrap items-end justify-between gap-6 pt-10">
              <div className="font-rhythm-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                <p>VOL. 1 — ISSUE 03 · OCTOBER 2026</p>
                <p className="mt-1.5">MORGAN CHANEL — EDITOR-IN-CHIEF</p>
                <p className="mt-1.5">$4.95 US · $5.95 CAN</p>
              </div>
              <div className="flex flex-col items-start gap-2 sm:items-end">
                <Barcode />
                <p className="font-rhythm-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                  ISSUE 003 · OCT 2026 · CITYGIRLSDECODED.COM
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* IN THIS ISSUE */}
        <section id="contents" data-testid="rhythm-toc" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
          <div className="flex items-end justify-between gap-6 border-b-4 border-[#F3F1ED] pb-5">
            <div>
              <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
                Issue 03 — October 2026
              </p>
              <h2 className="mt-3 font-rhythm-display text-3xl uppercase tracking-tight sm:text-4xl lg:text-5xl">
                In This Issue
              </h2>
            </div>
            <EqBars />
          </div>
          <div className="grid gap-x-14 sm:grid-cols-2">
            {toc.map((t, idx) => {
              const inner = (
                <>
                  <span className="flex min-w-0 items-baseline gap-4">
                    <span className="shrink-0 font-rhythm-mono text-[11px] font-bold text-[#F3F1ED]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 truncate font-rhythm-display text-base uppercase tracking-wide text-white/90 transition-colors group-hover:text-[#F3F1ED] sm:text-lg">
                      {t.label}
                    </span>
                  </span>
                  <span className="shrink-0 font-rhythm-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                    {t.kind}
                  </span>
                </>
              );
              const cls =
                "group flex min-w-0 items-baseline justify-between gap-4 border-b border-white/10 py-4 transition-colors hover:bg-white/[0.03]";
              return t.href.startsWith("/") ? (
                <Link key={t.label} data-testid={`rhythm-toc-item-${idx + 1}`} to={t.href} className={cls}>
                  {inner}
                </Link>
              ) : (
                <a key={t.label} data-testid={`rhythm-toc-item-${idx + 1}`} href={t.href} className={cls}>
                  {inner}
                </a>
              );
            })}
          </div>
        </section>

        {/* EDITOR'S LETTER */}
        <section data-testid="rhythm-editor-letter" className="mx-auto max-w-3xl px-5 pt-16 sm:pt-24">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="relative border border-white/15 p-8 sm:p-12"
          >
            <span className="absolute inset-x-0 top-0 h-1.5 bg-[#F3F1ED]" />
            <div className="flex items-center justify-between gap-4">
              <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
                From the Desk of Morgan Chanel
              </p>
              <PenLine className="h-4 w-4 shrink-0 text-white/40" />
            </div>
            <p className="mt-1.5 font-rhythm-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              Editor-in-Chief
            </p>
            <p className="drop-cap mt-7 text-base leading-relaxed text-white/80 sm:text-lg">
              Welcome to Issue 03. Fridays are for who's dropping and the debut
              numbers; Mondays are for the editor's notes — unfiltered, no PR
              filter, no scene politics.
              This issue's cover is a whole reclamation: K. Michelle took the
              training from FAMU, the pen from a decade of R&amp;B, and walked
              into Nashville with a country album — Jesus &amp; Whiskey. And the
              package inside still spans five rooms: Joyce Wrice
              claiming the city girl era, Dawn Richard building her own genre
              at Merge, Sheryl Crow pruning twelve albums deep, Jess Williamson
              writing the outlaw record Nashville won&apos;t, and Trippie Redd
              burying bangers in the middle of the warehouse.
            </p>
            <p className="mt-5 text-base leading-relaxed text-white/80 sm:text-lg">
              Put your headphones on. Let's get into it.
            </p>
            <p className="mt-7 font-garamond text-2xl italic text-[#F3F1ED]">— Morgan Chanel</p>
          </motion.div>
        </section>

        {featured && <FeaturedStory item={featured} />}

        {special && <SpecialFeature item={special} />}

        {items.length > 0 && (
          <div className="mx-auto max-w-7xl px-5 pt-10">
            <div
              data-testid="rhythm-coverlines"
              className="flex flex-wrap items-center gap-x-6 gap-y-2 border-y-4 border-[#F3F1ED] px-2 py-3"
            >
              <span className="font-rhythm-display text-sm uppercase tracking-wide text-[#F3F1ED]">
                Plus:
              </span>
              {items
                .filter((i) => i.kind === "friday" || i.kind === "spotlight")
                .slice(0, 4)
                .map((c) => (
                  <span
                    key={c.slug}
                    className="font-rhythm-display text-sm uppercase tracking-wide text-white/90"
                  >
                    {c.artist}: {c.title} <span className="text-[#F3F1ED]">★</span>
                  </span>
                ))}
            </div>
          </div>
        )}
        <RhythmSections items={items} tops={tops} />
        <RhythmReviews reviews={reviews} archived={archivedReviews} />
        <ChartWatch reviews={reviews} artPack={artPack} />
        <RhythmSubmit />
        <RhythmPress />
      </main>
    </div>
  );
}
